import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Separator } from '@/components/ui/separator'
import { Layers, SlidersHorizontal } from 'lucide-react'

interface MapLayer {
  id: string
  name: string
  enabled: boolean
}

interface MapFilter {
  status: {
    active: boolean
    idle: boolean
    offline: boolean
  }
  alertsOnly: boolean
  minSpeed: number
}

interface MapControlsProps {
  onLayersChange?: (layers: MapLayer[]) => void
  onFiltersChange?: (filters: MapFilter) => void
}

export function MapControls({ onLayersChange, onFiltersChange }: MapControlsProps) {
  const [layers, setLayers] = useState<MapLayer[]>([
    { id: 'vehicles', name: 'Vehicles', enabled: true },
    { id: 'routes', name: 'Routes', enabled: false },
    { id: 'geofences', name: 'Geofences', enabled: false },
    { id: 'traffic', name: 'Traffic', enabled: false },
    { id: 'weather', name: 'Weather', enabled: false },
  ])

  const [filters, setFilters] = useState<MapFilter>({
    status: {
      active: true,
      idle: true,
      offline: true,
    },
    alertsOnly: false,
    minSpeed: 0,
  })

  const handleLayerToggle = (layerId: string) => {
    const updated = layers.map(layer =>
      layer.id === layerId ? { ...layer, enabled: !layer.enabled } : layer
    )
    setLayers(updated)
    onLayersChange?.(updated)
  }

  const handleStatusFilterToggle = (status: keyof typeof filters.status) => {
    const updated = {
      ...filters,
      status: {
        ...filters.status,
        [status]: !filters.status[status],
      },
    }
    setFilters(updated)
    onFiltersChange?.(updated)
  }

  const handleAlertsOnlyToggle = () => {
    const updated = {
      ...filters,
      alertsOnly: !filters.alertsOnly,
    }
    setFilters(updated)
    onFiltersChange?.(updated)
  }

  const handleMinSpeedChange = (value: number[]) => {
    const updated = {
      ...filters,
      minSpeed: value[0],
    }
    setFilters(updated)
    onFiltersChange?.(updated)
  }

  return (
    <div className="absolute top-4 left-4 z-10 flex gap-2">
      {/* Layers Sheet */}
      <Sheet>
        <SheetTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
            className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
          >
            <Layers className="h-4 w-4 mr-2" />
            Layers
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-80">
          <SheetHeader>
            <SheetTitle>Map Layers</SheetTitle>
            <SheetDescription>
              Toggle map layers on and off
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {layers.map((layer) => (
              <div key={layer.id} className="flex items-center justify-between">
                <Label htmlFor={`layer-${layer.id}`} className="text-sm font-medium">
                  {layer.name}
                </Label>
                <Switch
                  id={`layer-${layer.id}`}
                  checked={layer.enabled}
                  onCheckedChange={() => handleLayerToggle(layer.id)}
                />
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Filters Sheet */}
      <Sheet>
        <SheetTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
            className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-80">
          <SheetHeader>
            <SheetTitle>Map Filters</SheetTitle>
            <SheetDescription>
              Filter vehicles displayed on the map
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Vehicle Status Filter */}
            <div className="space-y-4">
              <Label className="text-sm font-semibold">Vehicle Status</Label>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    <Label htmlFor="filter-active" className="text-sm">Active</Label>
                  </div>
                  <Switch
                    id="filter-active"
                    checked={filters.status.active}
                    onCheckedChange={() => handleStatusFilterToggle('active')}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-yellow-500" />
                    <Label htmlFor="filter-idle" className="text-sm">Idle</Label>
                  </div>
                  <Switch
                    id="filter-idle"
                    checked={filters.status.idle}
                    onCheckedChange={() => handleStatusFilterToggle('idle')}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-gray-500" />
                    <Label htmlFor="filter-offline" className="text-sm">Offline</Label>
                  </div>
                  <Switch
                    id="filter-offline"
                    checked={filters.status.offline}
                    onCheckedChange={() => handleStatusFilterToggle('offline')}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Alerts Only */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="filter-alerts" className="text-sm font-medium">
                  Alerts Only
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Show only vehicles with active alerts
                </p>
              </div>
              <Switch
                id="filter-alerts"
                checked={filters.alertsOnly}
                onCheckedChange={handleAlertsOnlyToggle}
              />
            </div>

            <Separator />

            {/* Minimum Speed */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Minimum Speed</Label>
                <span className="text-sm text-muted-foreground">{filters.minSpeed} mph</span>
              </div>
              <Slider
                value={[filters.minSpeed]}
                onValueChange={handleMinSpeedChange}
                max={100}
                step={5}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Filter vehicles moving slower than this speed
              </p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
